import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Search, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { EmptyState } from '@/shared/components/empty-state';
import { ApiError } from '@/shared/api/client';
import { formatDate, formatTime } from '@/shared/lib/format';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';
import { createSecurityException, deleteSecurityException, fetchSecurity } from '../api/use-observability';
import {
  SECURITY_CONTROLS,
  describeRow,
  filterRows,
  fixEffect,
  fixLabel,
  statusMeta,
  whoOf,
  type SecurityControlFilter,
} from '../model/security';
import type { SecurityException, SecurityFix, SecurityRow } from '../model/types';

const QUERY_KEY = ['observability-security'];

const TYPE_LABEL: Record<SecurityFix['type'], string> = { conta: 'Conta', ip: 'IP', user: 'Operador' };

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

function RowCard({ row, onFix }: { row: SecurityRow; onFix: (fix: SecurityFix) => void }) {
  const meta = statusMeta(row.status);
  const controlLabel = SECURITY_CONTROLS.find((item) => item.value === row.control)?.label ?? row.control;
  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge variant="outline">{controlLabel}</Badge>
            <Badge variant={meta.variant}>{meta.label}</Badge>
            <Badge variant="secondary" className="tabular-nums">
              {row.count.toLocaleString('pt-BR')}×
            </Badge>
          </div>
          <p className="truncate font-medium">{whoOf(row)}</p>
          <p className="break-all font-mono text-xs text-muted-foreground">
            {row.method ? `${row.method} ` : ''}
            {row.route || '—'} · IP {row.ip || '—'}
            {row.role ? ` · ${row.role}` : ''}
          </p>
          <p className="text-sm">{describeRow(row)}</p>
          {row.detail ? <p className="text-xs text-muted-foreground">{row.detail}</p> : null}
          <p className="text-xs tabular-nums text-muted-foreground">
            Última {formatDate(row.lastAt)} {formatTime(row.lastAt)} · primeira {formatDate(row.firstAt)}{' '}
            {formatTime(row.firstAt)}
          </p>
        </div>
        {row.fix ? (
          row.liberado ? (
            <Badge variant="success">Liberado</Badge>
          ) : (
            <Button size="sm" variant="outline" onClick={() => row.fix && onFix(row.fix)}>
              {fixLabel(row.fix)}
            </Button>
          )
        ) : null}
      </div>
    </Card>
  );
}

/**
 * Aba Segurança da Observabilidade: o que os ajustes do pentest recusaram em
 * produção (login, cadastro, escopo, chamadas sem token) e, para cada operador,
 * o botão que libera. Provisório: some quando as integrações estiverem ajustadas.
 */
export function SecurityPanel() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: QUERY_KEY, queryFn: fetchSecurity, refetchInterval: 15 * 1000 });
  const [control, setControl] = useState<SecurityControlFilter>('all');
  const [q, setQ] = useState('');
  const [pending, setPending] = useState<SecurityFix | null>(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const data = query.data;
  const rows = filterRows(data?.rows ?? [], control, q);
  const exceptions = data?.exceptions ?? [];

  const closeDialog = () => {
    setPending(null);
    setNote('');
  };

  const confirm = async () => {
    if (!pending) return;
    setSaving(true);
    try {
      await createSecurityException({ type: pending.type, value: pending.value, note: note.trim() || undefined });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success(`${fixLabel(pending)}: feito. Vale em até 1 segundo, sem reiniciar a API.`);
      closeDialog();
    } catch (err) {
      toast.error(errorMessage(err, 'Não foi possível criar a liberação.'));
    } finally {
      setSaving(false);
    }
  };

  const undo = async (item: SecurityException) => {
    try {
      await deleteSecurityException(item.id);
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success('Liberação desfeita.');
    } catch (err) {
      toast.error(errorMessage(err, 'Não foi possível desfazer a liberação.'));
    }
  };

  return (
    <>
      <Card className="mb-4 p-4 text-sm">
        <p className="font-medium">O que os ajustes do pentest recusaram</p>
        <p className="mt-1 text-muted-foreground">
          {data ? `Desde ${formatDate(data.since)} ${formatTime(data.since)}. ` : ''}A lista zera quando a API reinicia e
          se atualiza sozinha a cada 15 segundos. &quot;Passou&quot; é o que ainda funciona, mas pede atenção (credencial
          na URL, lista de empresas vazia, exceção engolida).
        </p>
        {data?.ipPrivado ? (
          <p className="mt-2 flex items-start gap-2 text-warning">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" />
            Há recusas com IP privado: o trust_proxy pode estar errado e todo mundo parece o mesmo IP.
          </p>
        ) : null}
      </Card>

      {exceptions.length > 0 ? (
        <Card className="mb-4 p-4">
          <p className="mb-2 text-sm font-medium">Liberações ativas</p>
          <ul className="divide-y divide-border/50">
            {exceptions.map((item) => (
              <li key={item.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                <div className="min-w-0">
                  <p className="break-all">
                    <Badge variant="outline" className="mr-2">
                      {TYPE_LABEL[item.type]}
                    </Badge>
                    <span className="font-mono text-xs">{item.value}</span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {item.createdBy || 'master'} · {formatDate(item.createdAt)} {formatTime(item.createdAt)}
                    {item.note ? ` · ${item.note}` : ''}
                  </p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => void undo(item)}>
                  Desfazer
                </Button>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      <div className="mb-4 grid gap-2 sm:flex sm:flex-wrap sm:items-center">
        <Select value={control} onValueChange={(value) => setControl(value as SecurityControlFilter)}>
          <SelectTrigger className="w-full sm:w-auto sm:min-w-[14rem]">
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            {SECURITY_CONTROLS.map((item) => (
              <SelectItem key={item.value} value={item.value}>
                {item.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="relative min-w-0 sm:w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(event) => setQ(event.target.value)}
            placeholder="E-mail, IP, rota ou usuário"
            className="pl-9"
            aria-label="Buscar por e-mail, IP, rota ou usuário"
          />
        </div>
      </div>

      {query.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-2xl" />
          ))}
        </div>
      ) : query.isError ? (
        <Card>
          <EmptyState
            icon={AlertTriangle}
            title="Não foi possível ler a aba"
            description={errorMessage(query.error, 'Tente de novo em instantes.')}
          />
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <EmptyState
            icon={ShieldCheck}
            title="Nada recusado neste filtro"
            description="Quando um ajuste do pentest recusar uma chamada, ela aparece aqui com o botão para liberar."
          />
        </Card>
      ) : (
        <div className="space-y-2">
          {rows.map((row) => (
            <RowCard
              key={[row.control, row.status, row.reason, row.method, row.route, row.actorId || row.email, row.ip, row.detail].join('|')}
              row={row}
              onFix={setPending}
            />
          ))}
        </div>
      )}

      <Dialog open={Boolean(pending)} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent>
          {pending ? (
            <>
              <DialogHeader>
                <DialogTitle>{fixLabel(pending)}</DialogTitle>
                <DialogDescription>{fixEffect(pending)}</DialogDescription>
              </DialogHeader>
              <p className="break-all rounded-xl border border-border/60 bg-muted/40 px-3 py-2 font-mono text-sm">
                {pending.value}
              </p>
              <div>
                <Label htmlFor="security-note">Motivo (opcional)</Label>
                <Input
                  id="security-note"
                  className="mt-1.5"
                  value={note}
                  maxLength={200}
                  onChange={(event) => setNote(event.target.value)}
                  placeholder="Ex.: integração do PDV que não consegue mandar token"
                />
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={closeDialog}>
                  Cancelar
                </Button>
                <Button loading={saving} onClick={() => void confirm()}>
                  Liberar
                </Button>
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
